import {
	addFileAttrsToUrl,
	type MediaBlobUrlAttrs,
	type MediaClient,
	type MediaStoreGetFileImageParams,
} from '@atlaskit/media-client';
import { type SSR } from '@atlaskit/media-common';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { SsrPreviewError } from '../SsrPreviewError';
import { type MediaFilePreview } from '../types';

import { mergeClientIdIntoAttrs } from './mergeClientIdIntoAttrs';

const applyCdnSigningParams = (url: string, cdnSigningParams?: Record<string, string>): string => {
	if (!cdnSigningParams || Object.keys(cdnSigningParams).length === 0) {
		return url;
	}
	try {
		const parsed = new URL(url);
		Object.entries(cdnSigningParams).forEach(([key, value]) => {
			parsed.searchParams.set(key, value);
		});
		return parsed.toString();
	} catch {
		return url;
	}
};

const getDataUri = (
	mediaClient: MediaClient,
	id: string,
	params: MediaStoreGetFileImageParams,
	mediaBlobUrlAttrs?: MediaBlobUrlAttrs,
	cdnSigningParams?: Record<string, string>,
) => {
	const rawDataURI = mediaClient.getImageUrlSync(id, params);
	const signedDataURI = applyCdnSigningParams(rawDataURI, cdnSigningParams);
	return mediaBlobUrlAttrs ? addFileAttrsToUrl(signedDataURI, mediaBlobUrlAttrs) : signedDataURI;
};

export const getSSRPreview = (
	ssr: SSR,
	mediaClient: MediaClient,
	id: string,
	params: MediaStoreGetFileImageParams,
	mediaBlobUrlAttrs?: MediaBlobUrlAttrs,
	cdnSigningParams?: Record<string, string>,
): MediaFilePreview => {
	try {
		// Synchronously extract clientId from initialAuth and merge into blob URL attrs
		const clientId = fg('platform_media_cross_client_copy_with_auth')
			? mediaClient.getClientIdSync()
			: undefined;
		const attrsWithClientId = mergeClientIdIntoAttrs(
			clientId,
			id,
			mediaBlobUrlAttrs,
			params.collection,
		);

		const dataURI = getDataUri(mediaClient, id, params, attrsWithClientId, cdnSigningParams);
		let srcSet = `${dataURI} 1x`;

		if (params.width) {
			const doubleDataURI = getDataUri(
				mediaClient,
				id,
				{ ...params, width: params.width * 2, height: params.height && params.height * 2 },
				attrsWithClientId,
				cdnSigningParams,
			);
			// We want to embed some meta context into dataURI for Copy/Paste to work.
			srcSet += `, ${doubleDataURI} 2x`;
		}
		const source = ssr === 'client' ? 'ssr-client' : 'ssr-server';
		return { dataURI, source, orientation: 1, srcSet };
	} catch (e) {
		const reason = ssr === 'server' ? 'ssr-server-uri' : 'ssr-client-uri';
		throw new SsrPreviewError(reason, e instanceof Error ? e : undefined);
	}
};
